import { useState } from 'react'
import { skipToken } from '@reduxjs/toolkit/query'
import { useParams } from 'react-router-dom'
import { SELECTED_FILTERS_NO_DATA_MESSAGE } from 'constants/filters'
import { toast } from 'react-toastify'
import {
  BarLegendState,
  HorizontalBarChart,
  HorizontalBarChartProps,
  InputField,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Spinner,
  TIMP_CHART_COLORS,
  useHorizontalBarChartPngExport,
  usePngExport
} from '@jana-technology/platform-ui'

import { SortChart, ThreatsChartView } from '$/types'
import { useAppSelector } from 'app/hooks'
import {
  IQRAGraphCsvExportParams,
  useExportQRARiskByThreatGraphMutation
} from '_/src/app/services/api/qra/graphs'
import { useBaselineInfoQuery } from 'app/services/api/baselines'
import { useGetQRAAssessmentQuery } from 'app/services/api/qra/assessments'
import { useExportJobContext } from 'common/contexts/useExportJobContext'
import {
  formatWithCommasOrExponential,
  getChartLabelBasedOnRiskUnit,
  makeRUoptions
} from 'common/utils/chart/chartUtils'

import {
  IChartData,
  IThreatCategoryTooltipGroup
} from 'features/RiskManagement/Components/Graphs'
import GraphContainerCard from 'features/RiskManagement/Components/Graphs/Components/GraphContainerCard'
import { selectRiskUnit } from 'features/RiskManagement/Components/LocalFilter/localFilterSelectors'

import {
  getRiskByThreatCsvExportQueryParams,
  sortChartRows
} from './Chart.helpers'

const EXPORT_UNAVAILABLE_MESSAGE =
  'Chart image export is unavailable right now.'

const THREAT_TOOLTIP_ROW_CLASS_NAME = 'flex items-center gap-x-1'
const THREAT_TOOLTIP_LABEL_CLASS_NAME = 'min-w-0 flex-1 truncate pl-2'
const THREAT_TOOLTIP_VALUE_CLASS_NAME = 'w-16 shrink-0 text-right tabular-nums'
const THREAT_TOOLTIP_HEADER_VALUE_CLASS_NAME =
  'flex w-16 shrink-0 justify-end gap-2'

type ThreatsChartDatum = {
  category: string
  tooltipGroup?: IThreatCategoryTooltipGroup
  [key: string]: number | string | IThreatCategoryTooltipGroup | undefined
}

type ThreatsChartSeries = {
  key: string
  values: number[]
}

type ThreatsChartRow = ThreatsChartDatum & {
  originalIndex: number
  primaryValue?: number
}

interface IThreatsChart {
  showLoading?: boolean
  threatData: IChartData
  threatCategoryData?: IChartData
  groupingKey?: string
  csvExportParams?: IQRAGraphCsvExportParams
}

const CHART_TITLE = 'Risk by Threat'
const CHART_COLORS = [...TIMP_CHART_COLORS]

const ExportableHorizontalBarChart = <D extends object>(
  props: HorizontalBarChartProps<D>
) => {
  const parentRef = useHorizontalBarChartPngExport(props)

  return (
    <div
      ref={parentRef}
      className='h-full w-full min-w-0 [&_.visx-legend-item]:pointer-events-none [&_.visx-legend-item]:!cursor-default'
    >
      <HorizontalBarChart<D> {...props} />
    </div>
  )
}

const ThreatsChart = ({
  showLoading = false,
  threatData,
  threatCategoryData,
  groupingKey,
  csvExportParams
}: IThreatsChart) => {
  const riskUnit = useAppSelector(selectRiskUnit)
  const { downloadImage, isReady: isChartPngExportReady } = usePngExport()

  const [chartView, setChartView] =
    useState<ThreatsChartView>('threat-category')

  const [sortBy, setSortBy] = useState<SortChart>('default')
  const activeThreatData =
    chartView === 'threat-category'
      ? (threatCategoryData ?? threatData)
      : threatData

  const { assessmentId } = useParams()

  const { currentData: currentAssessment } = useGetQRAAssessmentQuery(
    assessmentId ?? skipToken
  )

  const stateNames = Object.keys(activeThreatData.values)
  const primaryStateName = stateNames[0]
  const secondaryStateName = stateNames[1]
  const chartSeries: ThreatsChartSeries[] = []

  if (primaryStateName != null) {
    chartSeries.push({
      key: activeThreatData.labels[primaryStateName] || 'Series 1',
      values: activeThreatData.values[primaryStateName] ?? []
    })
  }

  if (secondaryStateName != null) {
    chartSeries.push({
      key: activeThreatData.labels[secondaryStateName] || 'Series 2',
      values: activeThreatData.values[secondaryStateName] ?? []
    })
  }

  const shouldShowThreatCategoryNoData =
    chartView === 'threat-category' &&
    !showLoading &&
    (activeThreatData.categories.length < 1 || chartSeries.length < 1)

  const hasComparisonSeries = chartSeries.length > 1
  const legendState: BarLegendState = {
    activeColors: CHART_COLORS.slice(0, chartSeries.length),
    activeDataKeys: chartSeries.map((series) => series.key),
    inactiveItems: [],
    legendColors: CHART_COLORS,
    toggleItem: () => undefined,
    highlightItem: () => undefined,
    clearHighlightedItem: () => undefined
  }
  const tooltipSeries = stateNames.map((stateName, index) => ({
    color: CHART_COLORS[index],
    label:
      activeThreatData.labels[stateName] ||
      chartSeries[index]?.key ||
      stateName,
    stateName
  }))
  const chartRows = activeThreatData.categories.map<ThreatsChartRow>(
    (category, index) => {
      const row: ThreatsChartRow = {
        category,
        originalIndex: index,
        primaryValue: chartSeries[0]?.values[index],
        tooltipGroup: activeThreatData.threatCategoryTooltipGroups?.[index]
      }

      for (const series of chartSeries) {
        row[series.key] = series.values[index]
      }

      return row
    }
  )
  const chartData = sortChartRows(chartRows, sortBy).map(
    (row): ThreatsChartDatum => {
      const datum: ThreatsChartDatum = {
        category: row.category,
        ...(row.tooltipGroup == null ? {} : { tooltipGroup: row.tooltipGroup })
      }

      for (const series of chartSeries) {
        datum[series.key] = row[series.key]
      }

      return datum
    }
  )

  const formatterOptions = makeRUoptions('ru')
  const formatThreatValue = (value: number) =>
    formatWithCommasOrExponential(value, formatterOptions)
  const renderThreatCategoryTooltip =
    chartView === 'threat-category'
      ? (tooltipProps: { datum: ThreatsChartDatum }) => {
          const tooltipGroup = tooltipProps.datum.tooltipGroup

          if (tooltipGroup == null) {
            return null
          }

          return (
            <div className='max-w-[32rem] rounded bg-white px-3 py-2 text-sm leading-6 shadow-tooltip'>
              <div className='font-semibold'>{tooltipGroup.label}</div>
              <div
                className={`text-grey-600 mt-1 ${THREAT_TOOLTIP_ROW_CLASS_NAME}`}
              >
                <div className='min-w-0 flex-1 pl-2 font-medium tracking-wide'>
                  By Subthreat
                </div>
                {hasComparisonSeries &&
                  tooltipSeries.map((series) => (
                    <div
                      key={series.stateName}
                      className={`${THREAT_TOOLTIP_HEADER_VALUE_CLASS_NAME} ${
                        tooltipSeries[0]?.stateName === series.stateName
                          ? 'pl-4'
                          : ''
                      }`}
                    >
                      <div
                        className='size-3 rounded-sm'
                        aria-label={`${series.label} color indicator`}
                        style={{ backgroundColor: series.color }}
                      />
                    </div>
                  ))}
              </div>
              <div className='mt-1 space-y-1'>
                {tooltipGroup.members.map((member) => (
                  <div
                    key={member.key}
                    className={THREAT_TOOLTIP_ROW_CLASS_NAME}
                  >
                    <div className={THREAT_TOOLTIP_LABEL_CLASS_NAME}>
                      {member.label}
                    </div>
                    {tooltipSeries.map((series) => (
                      <div
                        key={`${member.key}-${series.stateName}`}
                        className={`${THREAT_TOOLTIP_VALUE_CLASS_NAME} ${
                          tooltipSeries[0]?.stateName === series.stateName
                            ? 'pl-4'
                            : ''
                        }`}
                      >
                        {formatThreatValue(
                          member.values[series.stateName] ?? 0
                        )}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
              <div
                className={`${THREAT_TOOLTIP_ROW_CLASS_NAME} mt-1 font-semibold`}
              >
                <div className='min-w-0 flex-1'>Total Risk</div>
                {tooltipSeries.map((series) => (
                  <div
                    key={`total-${series.stateName}`}
                    className={`${THREAT_TOOLTIP_VALUE_CLASS_NAME} ${
                      tooltipSeries[0]?.stateName === series.stateName
                        ? 'pl-4'
                        : ''
                    }`}
                  >
                    {formatThreatValue(
                      tooltipGroup.totals[series.stateName] ?? 0
                    )}
                  </div>
                ))}
              </div>
            </div>
          )
        }
      : undefined

  const getFileNameForExport = (chartView: ThreatsChartView) => {
    let fileName =
      chartView === 'threat-category'
        ? 'Risk_By_Threat_ThreatCategory'
        : 'Risk_By_Threat_Subthreats'

    if (currentAssessment?.name) {
      fileName += `_${currentAssessment.name}`
    }

    return fileName
  }

  const handleExportChartAsPng = async (chartView: ThreatsChartView) => {
    const fileName = getFileNameForExport(chartView)

    if (!isChartPngExportReady) {
      toast.error(EXPORT_UNAVAILABLE_MESSAGE)

      return
    }

    try {
      await downloadImage({ fileName, options: { title: CHART_TITLE } })
    } catch {
      toast.error(EXPORT_UNAVAILABLE_MESSAGE)
    }
  }

  const { currentData: baseline } = useBaselineInfoQuery('current')

  const [triggerExportRiskByThreatGraph] =
    useExportQRARiskByThreatGraphMutation()

  const { createDataExportJob } = useExportJobContext()

  const handleExportChartAsCsv = async (chartView: ThreatsChartView) => {
    const fileName = getFileNameForExport(chartView)
    const baselineId = baseline?.id

    if (csvExportParams) {
      createDataExportJob(
        triggerExportRiskByThreatGraph,
        getRiskByThreatCsvExportQueryParams({
          sortBy,
          chartView,
          groupingKey,
          csvExportParams,
          assessmentId,
          baselineId
        }),
        fileName,
        '.csv'
      )
    }
  }

  return (
    <GraphContainerCard
      title={CHART_TITLE}
      optionsComponent={
        <CardActions
          chartView={chartView}
          setChartView={setChartView}
          sortBy={sortBy}
          setSortBy={setSortBy}
        />
      }
      actionMenuItems={[
        {
          children: 'Export chart as image',
          onSelect: () => {
            void handleExportChartAsPng(chartView)
          }
        },
        {
          children: 'Export chart as data',
          onSelect: () => handleExportChartAsCsv(chartView)
        }
      ]}
    >
      {shouldShowThreatCategoryNoData || showLoading ? (
        showLoadingOrThreatCategoryNoData(
          showLoading,
          shouldShowThreatCategoryNoData
        )
      ) : (
        <ExportableHorizontalBarChart
          height={hasComparisonSeries ? 350 : 390}
          barWidth={hasComparisonSeries ? 6 : 16}
          groupInnerGap={4}
          chartName={CHART_TITLE}
          dataKeys={chartSeries.map((series) => series.key)}
          data={chartData}
          xAccessor={(datum) => datum.category}
          xAxisLabel={getChartLabelBasedOnRiskUnit(riskUnit)}
          xTicks={3}
          xTickFormatter={(val: number) => {
            return formatThreatValue(val)
          }}
          renderTooltip={renderThreatCategoryTooltip}
          withScroll={true}
          legendAnchor='container-start'
          withLegend={hasComparisonSeries}
          legendState={legendState}
          colors={CHART_COLORS}
        />
      )}
    </GraphContainerCard>
  )
}

const showLoadingOrThreatCategoryNoData = (
  showLoading: boolean,
  shouldShowThreatCategoryNoData: boolean
) => {
  if (showLoading) {
    return (
      <div className='flex h-full items-center justify-center text-center'>
        <Spinner label='Loading...' size='medium' />
      </div>
    )
  }

  if (shouldShowThreatCategoryNoData) {
    return (
      <div className='flex h-full items-center justify-center text-center'>
        {SELECTED_FILTERS_NO_DATA_MESSAGE}
      </div>
    )
  }

  return null
}

const CardActions = ({
  chartView,
  setChartView,
  sortBy,
  setSortBy
}: {
  chartView: ThreatsChartView
  setChartView: (value: ThreatsChartView) => void
  sortBy: SortChart
  setSortBy: (value: SortChart) => void
}) => (
  <>
    <InputField direction='row' className='p-4 text-sm'>
      <Label htmlFor='threats-chart-sort-select'>Sort:</Label>
      <Select value={sortBy} onValueChange={setSortBy}>
        <SelectTrigger id='threats-chart-sort-select' className='h-8 w-36'>
          <SelectValue />
        </SelectTrigger>
        <SelectContent className='text-sm'>
          <SelectItem value='default'>Default</SelectItem>
          <SelectItem value='risk-asc'>Risk ↑</SelectItem>
          <SelectItem value='risk-desc'>Risk ↓</SelectItem>
          <SelectItem value='alphabetical-asc'>Alphabetical ↑</SelectItem>
          <SelectItem value='alphabetical-desc'>Alphabetical ↓</SelectItem>
        </SelectContent>
      </Select>
    </InputField>
    <Select value={chartView} onValueChange={setChartView}>
      <SelectTrigger className='h-8 w-[152px] text-sm'>
        <SelectValue />
      </SelectTrigger>
      <SelectContent className='text-sm'>
        <SelectItem value='threat-category'>Threat Category</SelectItem>
        <SelectItem value='threat'>Threat</SelectItem>
      </SelectContent>
    </Select>
  </>
)

export default ThreatsChart
